import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import { IconChevronDown } from '@tabler/icons';

import api from '../../../services/ApiManagement/Index';
import { useOnlineStatus } from '../../../hooks/onlineStatus';
import { useToast } from '../../../hooks/toast';
import fileSaver from '../../../utils/fileSaver';

import BackHeader from '../../../components/BackHeader';
import Offline from '../../../components/OfflineMessage';
import Button from '../../../components/Button';
import {
  Label,
  Select,
  InputLabel,
  SelectContainer,
  SelectIconWrapper,
  Row,
} from '../../../components/Generic';
import {
  Container,
  Content,
  FiltersGrid,
  SwitchContainer,
  SwitchLabel,
  SwitchSlider,
  Switch,
  SwitchInputElement,
} from './styles';

interface IFilters {
  estado: string;
  sexo: string;
  ano: string;
  raça: string;
  categoria: string;
  safra: string;
  lote: string;
  pertencente: string;
}

const ExportData: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [validFilters, setValidFilters] = useState<any>([]);
  const [fileType, setFileType] = useState<'xlsx' | 'csv'>('xlsx');
  const [ultimas24h, setUltimas24h] = useState(false);
  const [filters, setFilters] = useState<IFilters>({
    estado: 'ativo',
    sexo: '',
    ano: '',
    raça: '',
    categoria: '',
    safra: '',
    lote: '',
    pertencente: 'true',
  });

  const { id_property }: any = useParams();
  const isOnline = useOnlineStatus();
  const { addToast } = useToast();

  useEffect(() => {
    getValidFilters();
  }, []);

  const getValidFilters = async () => {
    try {
      const filtersResponse = await api.get(
        `/animais/filtros/validos`,
        isOnline,
        'filtrosValidos',
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      setValidFilters(filtersResponse.data);
    } catch (err) {
      console.log(err);
    }
  };

  const exportData = async () => {
    try {
      setLoading(true);

      let params: any = {
        estado: filters.estado,
        sexo: filters.sexo,
        raça: filters.raça,
        ano: filters.ano,
        safra: filters.safra,
        categoria: filters.categoria,
        lote: filters.lote,
        pertencente: filters.pertencente === 'true',
      };

      Object.keys(params).forEach(key =>
        params[key] === '' ? delete params[key] : {},
      );

      //console.log(params);

      const exportResponse = await api.post(
        `/animais/relatorio/.${fileType}${
          fileType === 'xlsx' ? '/registros' : ''
        }`,
        {
          pertencente: params.pertencente,
          ultimas24h: ultimas24h ? true : undefined,
        },
        isOnline,
        {
          responseType: 'blob',
          headers: {
            propriedade_id: id_property,
          },
          params,
        },
      );

      await fileSaver(
        exportResponse.data,
        `relatorio-${moment().format('DD-MM-YYYY')}.${fileType}`,
      );

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Relatório gerado.',
      });
    } catch (err) {
      console.log(err);

      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response?.data?.message || 'Algo deu errado!',
      });
    } finally {
      setLoading(false);
    }
  };

  const genderOptions = [
    { value: '', label: 'Todos' },
    { value: 'macho', label: 'Macho' },
    { value: 'femea', label: 'Fêmea' },
  ];

  const stateOptions = [
    { value: 'ativo', label: 'Ativos' },
    { value: 'inativo', label: 'Inativos' },
  ];

  const relationOptions = [
    { value: 'true', label: 'Pertencentes' },
    { value: 'false', label: 'Externos' },
  ];

  return (
    <Container>
      <BackHeader title="Gerar relatório" />

      {!isOnline ? (
        <Offline />
      ) : (
        <Content>
          <Label>Filtros</Label>
          <FiltersGrid>
            <div>
              <InputLabel>Estado</InputLabel>

              <SelectContainer>
                <Select
                  onChange={(e: any) =>
                    setFilters({ ...filters, estado: e.target.value })
                  }
                  value={filters.estado}
                >
                  {stateOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <SelectIconWrapper style={{ right: 20 }}>
                  <IconChevronDown size={20} color="#999" />
                </SelectIconWrapper>
              </SelectContainer>
            </div>

            <div>
              <InputLabel>Sexo</InputLabel>

              <SelectContainer>
                <Select
                  onChange={(e: any) =>
                    setFilters({ ...filters, sexo: e.target.value })
                  }
                  value={filters.sexo}
                >
                  {genderOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <SelectIconWrapper style={{ right: 20 }}>
                  <IconChevronDown size={20} color="#999" />
                </SelectIconWrapper>
              </SelectContainer>
            </div>

            <div>
              <InputLabel>Relação</InputLabel>

              <SelectContainer>
                <Select
                  onChange={(e: any) =>
                    setFilters({ ...filters, pertencente: e.target.value })
                  }
                  value={filters.pertencente}
                >
                  {relationOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <SelectIconWrapper style={{ right: 20 }}>
                  <IconChevronDown size={20} color="#999" />
                </SelectIconWrapper>
              </SelectContainer>
            </div>

            <div>
              <InputLabel>Raça</InputLabel>

              <SelectContainer>
                <Select
                  onChange={(e: any) =>
                    setFilters({ ...filters, raça: e.target.value })
                  }
                  value={filters.raça}
                >
                  <option value="">Todas</option>
                  {validFilters.racas?.map(
                    (option: any) =>
                      option && (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ),
                  )}
                </Select>
                <SelectIconWrapper style={{ right: 20 }}>
                  <IconChevronDown size={20} color="#999" />
                </SelectIconWrapper>
              </SelectContainer>
            </div>

            <div>
              <InputLabel>Ano de nascimento</InputLabel>

              <SelectContainer>
                <Select
                  onChange={(e: any) =>
                    setFilters({ ...filters, ano: e.target.value })
                  }
                  value={filters.ano}
                >
                  <option value="">Todos</option>
                  {validFilters.datas_de_nascimento
                    ?.sort((a: any, b: any) => (a > b && 1) || -1)
                    .map(
                      (option: any) =>
                        option && (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ),
                    )}
                </Select>
                <SelectIconWrapper style={{ right: 20 }}>
                  <IconChevronDown size={20} color="#999" />
                </SelectIconWrapper>
              </SelectContainer>
            </div>

            <div>
              <InputLabel>Safra</InputLabel>

              <SelectContainer>
                <Select
                  onChange={(e: any) =>
                    setFilters({ ...filters, safra: e.target.value })
                  }
                  value={filters.safra}
                >
                  <option value="">Todas</option>
                  {validFilters.safras
                    ?.sort((a: any, b: any) => (a > b && 1) || -1)
                    .map(
                      (option: any) =>
                        option && (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ),
                    )}
                </Select>
                <SelectIconWrapper style={{ right: 20 }}>
                  <IconChevronDown size={20} color="#999" />
                </SelectIconWrapper>
              </SelectContainer>
            </div>

            <div>
              <InputLabel>Categoria</InputLabel>

              <SelectContainer>
                <Select
                  onChange={(e: any) =>
                    setFilters({ ...filters, categoria: e.target.value })
                  }
                  value={filters.categoria}
                >
                  <option value="">Todas</option>

                  {validFilters.categorias?.map(
                    (option: any) =>
                      option && (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ),
                  )}
                </Select>
                <SelectIconWrapper style={{ right: 20 }}>
                  <IconChevronDown size={20} color="#999" />
                </SelectIconWrapper>
              </SelectContainer>
            </div>

            <div>
              <InputLabel>Lote</InputLabel>

              <SelectContainer>
                <Select
                  onChange={(e: any) =>
                    setFilters({ ...filters, lote: e.target.value })
                  }
                  value={filters.lote}
                >
                  <option value="">Todos</option>
                  {validFilters.lotes?.map(
                    (option: any) =>
                      option && (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ),
                  )}
                </Select>
                <SelectIconWrapper style={{ right: 20 }}>
                  <IconChevronDown size={20} color="#999" />
                </SelectIconWrapper>
              </SelectContainer>
            </div>
          </FiltersGrid>

          <InputLabel>Formato</InputLabel>

          <Row style={{ marginBottom: 40, marginTop: 10 }}>
            <div style={{ marginRight: 20 }}>
              <input
                type="radio"
                name="filetype"
                id="xlsx"
                value="xlsx"
                checked={fileType === 'xlsx'}
                onChange={(e: any) => setFileType(e.target.value)}
              />
              <label htmlFor="xlsx">.xlsx</label>
            </div>

            <div>
              <input
                type="radio"
                name="filetype"
                id="csv"
                value="csv"
                checked={fileType === 'csv'}
                onChange={(e: any) => setFileType(e.target.value)}
              />
              <label htmlFor="csv">.csv</label>
            </div>
          </Row>

          <SwitchContainer>
            <SwitchLabel>Apenas dados das últimas 24h</SwitchLabel>

            <Switch>
              <SwitchInputElement
                type="checkbox"
                onClick={() => setUltimas24h(!ultimas24h)}
              />

              <SwitchSlider />
            </Switch>
          </SwitchContainer>

          <Button
            style={{ marginBottom: 20 }}
            isLoading={loading ? true : undefined}
            onClick={exportData}
          >
            Exportar dados
          </Button>
        </Content>
      )}
    </Container>
  );
};

export default ExportData;
